import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useFindAllCompaniesQuery, useRestoreCompanyMutation } from "../../../features/companyApi";
import { useFindAllPeopleQuery, useRestorePersonMutation } from "../../../features/peopleApi";
import { ContactType, FindAllContactsFilters, FindContactsDatabaseResponse } from "../../lib/filters/dto";
import { initialStateContactsFilters } from "../../lib/filters/filtersSlice";
import { Preferences } from "../../lib/preferences";
import { PreferencesSection } from "../../lib/preferences/dto";
import { useAppDispatch, useAppSelector } from "../../lib/redux/hooks";
import { BuildingIcon } from "../../ui/icons/building";
import { DownloadIcon } from "../../ui/icons/download";
import { TrashIcon } from "../../ui/icons/trash";
import { UserPlusIcon } from "../../ui/icons/userPlus";
import { UsersIcon } from "../../ui/icons/users";
import { Layout } from "../../ui/layout";
import Button from "../../ui/molecules/button";
import ButtonDropdown from "../../ui/molecules/buttonDropdown";
import { Spinner } from "../../ui/molecules/spinner";
import { MenuItems } from "../../ui/organisms/navbar/dto";
import { TabMenu } from "../../ui/organisms/tabs/tabMenu";
import { TabPages } from "../../ui/organisms/tabs/tabPages";
import { DeleteContact } from "../database/commons/delete";
import { DeleteBulkContacts } from "../database/commons/deleteBulk";
import { DownloadBulk } from "../database/commons/downloadBulk";
import { useDownloadCsv } from "../database/commons/useDownloadCsv";
import { Companies } from "../database/companies/list";
import { People } from "../database/people/list";
import { setRestoreContactModal } from "../database/slice";
import { addBannerWithTimeout } from "../../lib/centralizedBanner/slice";
import { ConfirmModal } from "../../ui/organisms/confirmModal.tsx";
import { keycloak } from "../../lib/keycloak";

export function VerifyBins() {
    const { tab } = useParams();
    const databaseState = useAppSelector(state => state.database)

    const [direction, setDirection] = useState<'left' | 'right'>("left")

    const [peopleFilters, setPeopleFilters] = useState<FindAllContactsFilters>(initialStateContactsFilters['verify-list'][ContactType.PERSON]);
    const { data: peopleData, isLoading: isLoadingPeople } = useFindAllPeopleQuery(peopleFilters);
    const [peopleSelectedIds, setPeopleSelectedIds] = useState<string[]>([]);
    const downloadPeopleCsv = useDownloadCsv(peopleSelectedIds, setPeopleSelectedIds, ContactType.PERSON);
    const [restorePersons, { isLoading: restorePersonsLoading }] = useRestorePersonMutation();
    const [restoreCompany, { isLoading: restoreCompanyLoading }] = useRestoreCompanyMutation();

    const handleRestorePersons = async () => {
        try {
            await restorePersons({ ids: peopleSelectedIds }).unwrap();
            setPeopleSelectedIds([]);
            dispatch(setRestoreContactModal(false));
            dispatch(addBannerWithTimeout({
                message: "Persone ripristinate con successo.",
                variant: "success"
            }))
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore durante il ripristino delle persone.",
                variant: "error"
            }))
        }
    }

    const handleRestoreCompanies = async () => {
        try {
            await restoreCompany({ ids: companiesSelectedIds }).unwrap();
            setCompaniesSelectedIds([]);
            dispatch(setRestoreContactModal(false));
            dispatch(addBannerWithTimeout({
                message: "Aziende ripristinate con successo.",
                variant: "success"
            }))
        } catch (error) {
            dispatch(addBannerWithTimeout({
                message: "Si è verificato un errore durante il ripristino delle aziende.",
                variant: "error"
            }))
        }
    }

    const [companiesFilters, setCompaniesFilters] = useState<FindAllContactsFilters>(initialStateContactsFilters['verify-list'][ContactType.COMPANY]);
    const { data: companiesData, isLoading: isLoadingCompanies } = useFindAllCompaniesQuery(companiesFilters);
    const [companiesSelectedIds, setCompaniesSelectedIds] = useState<string[]>([]);
    const downloadCompaniesCsv = useDownloadCsv(companiesSelectedIds, setCompaniesSelectedIds, ContactType.COMPANY);

    const tabValue = useParams().tab
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const [showDeleteContactModal, setShowDeleteContactModal] = useState(false);
    const [showDeleteBulkContactsModal, setShowDeleteBulkContactsModal] = useState(false);
    const [showDownloadBulkContactsModal, setShowDownloadBulkContactsModal] = useState(false);

    if (isLoadingPeople || isLoadingCompanies) {
        return <div className="flex items-center justify-center h-screen w-full"><Spinner /></div>
    }

    return (
        <Layout
            headerLabel={"Cestino da verificare"}
            breadcrumbItems={['Cestino Da Verificare']}
            menuItem={MenuItems.VERIFY_BINS}
            headerChildren={
                <div className="flex items-center justify-between">
                    <TabMenu
                        direction={direction}
                        setDirection={(value) => setDirection(value)}
                        setValue={(e) => {
                            if (e === 0) {
                                navigate(window.location.pathname.replace(/\/specifics|\/companies/, "/people"));
                            }
                            if (e === 1) {
                                navigate(window.location.pathname.replace(/\/specifics|\/people/, "/companies"));
                            }
                        }}
                        value={tabValue === 'companies' ? 1 : 0}
                        items={[
                            {
                                icon: <UsersIcon color={""} size={0} />,
                                label: "Persone",
                            },
                            {
                                icon: <BuildingIcon color={""} size={0} />,
                                label: "Aziende",
                            },
                        ]}
                    />
                    <div className="flex gap-2">
                        {
                            !keycloak.hasRealmRole('commercial') &&
                            <Button
                                disabled={(tabValue === 'companies' && companiesSelectedIds.length <= 0) || (tabValue === 'people' && peopleSelectedIds.length <= 0)}
                                size={"sm"}
                                iconPosition={"left"}
                                variant={"outline"}
                                color={"blue"}
                                label="Ripristina"
                                icon={<UserPlusIcon color={""} size={0} />}
                                onClick={() => { dispatch(setRestoreContactModal(true)) }}
                            />
                        }
                        {
                            !keycloak.hasRealmRole('commercial') &&
                            ((companiesData?.companies?.total !== 0 && tabValue === 'companies') || (tabValue === 'people' && peopleData?.people?.total !== 0)) &&
                            (
                                downloadPeopleCsv.loading || downloadCompaniesCsv.loading ?
                                    <Spinner /> :
                                    <ButtonDropdown
                                        disabled={(tabValue === 'people' && peopleSelectedIds.length <= 0) || (tabValue === 'companies' && companiesSelectedIds.length <= 0)}
                                        options={[
                                            {
                                                label: 'Esporta tutti',
                                                onClick: () => {
                                                    setShowDownloadBulkContactsModal(true)
                                                }
                                            }
                                        ]}
                                        size={"sm"}
                                        iconPosition={"left"}
                                        color={"blue"}
                                        onClick={() => tabValue === 'people' ? downloadPeopleCsv.handleDownloadConfirm() : downloadCompaniesCsv.handleDownloadConfirm()}
                                        label="Esporta"
                                        icon={<DownloadIcon color={""} size={0} />}
                                    />
                            )
                        }
                        {
                            ((companiesData?.companies?.total !== 0 && tabValue === 'companies') || (tabValue === 'people' && peopleData?.people?.total !== 0)) &&
                            !window.location.pathname.includes('black-list') &&
                            !keycloak.hasRealmRole('commercial') &&
                            <ButtonDropdown
                                disabled={(tabValue === 'people' && peopleSelectedIds.length <= 0) || (tabValue === 'companies' && companiesSelectedIds.length <= 0)}
                                icon={<TrashIcon color={""} size={0} />}
                                options={[{
                                    label: 'Elimina tutti',
                                    onClick: () => {
                                        setShowDeleteBulkContactsModal(true)
                                    }
                                }
                                ]}
                                onClick={() => setShowDeleteContactModal(true)}
                                label="Elimina"
                                size={"sm"}
                                iconPosition={"left"}
                                color={"blue"}
                            />
                        }
                    </div>
                </div>
            }
        >
            <div className="flex max-w-full w-full max-h-full h-full overflow-hidden p-4">
                <div className="flex max-w-full w-full h-full rounded-xl flex-col relative rounded-t-xl bg-white shadow-sm ">
                    <div className="max-h-full max-w-full h-full rounded-xl">
                        <TabPages
                            direction={direction}
                            setDirection={(value) => setDirection(value)}
                            pages={[
                                <People
                                    section="verify-list"
                                    selectedIds={peopleSelectedIds}
                                    setSelectedIds={(ids) => {
                                        setPeopleSelectedIds(ids)
                                    }}
                                    contacts={peopleData?.people as unknown as FindContactsDatabaseResponse<ContactType.PERSON>}
                                    onPageChange={(page) => setPeopleFilters((f) => ({ ...f, page }))}
                                    onItemsPerPageChange={(itemsPerPage) => setPeopleFilters((f) => ({ ...f, itemsPerPage, page: 0 }))}
                                    filters={peopleFilters}
                                    onChangeFilters={f => setPeopleFilters(prevFilters => ({ ...prevFilters, ...f, page: 0 }))}
                                    toActivate={peopleData?.toActivate ?? 0}
                                    key={0}
                                />,
                                <Companies
                                    section="verify-list"
                                    selectedIds={companiesSelectedIds}
                                    setSelectedIds={(ids) => {
                                        setCompaniesSelectedIds(ids)
                                    }}
                                    contacts={companiesData?.companies as unknown as FindContactsDatabaseResponse<ContactType.COMPANY>}
                                    onPageChange={(page) => setCompaniesFilters((f) => ({ ...f, page }))}
                                    onItemsPerPageChange={(itemsPerPage) => setCompaniesFilters((f) => ({ ...f, itemsPerPage, page: 0 }))}
                                    filters={companiesFilters}
                                    onChangeFilters={f => setCompaniesFilters(prevFilters => ({ ...prevFilters, ...f, page: 0 }))}
                                    toActivate={companiesData?.toActivate ?? 0}
                                    key={1}
                                />
                            ]}
                            value={tabValue === 'companies' ? 1 : 0}
                            setValue={(e) => {
                                if (e === 0) {
                                    navigate(window.location.pathname.replace(/\/specifics|\/companies/, "/people"));
                                }
                                if (e === 1) {
                                    navigate(window.location.pathname.replace(/\/specifics|\/people/, "/companies"));
                                }
                            }}
                        />
                    </div>
                </div>
                <Preferences section={tabValue === 'people' ? PreferencesSection.toVerifyBinPersonPreferences : PreferencesSection.toVerifyBinCompanyPreferences} key={tabValue === 'people' ? PreferencesSection.toVerifyBinPersonPreferences : PreferencesSection.toVerifyBinCompanyPreferences} />
                <DownloadBulk
                    contactType={tabValue === 'people' ? ContactType.PERSON : ContactType.COMPANY}
                    section="verify-list"
                    filters={tabValue === 'people' ? peopleFilters : companiesFilters}
                    setFilters={tabValue === 'people' ? setPeopleFilters : setCompaniesFilters}
                    showModal={showDownloadBulkContactsModal}
                    setShowModal={setShowDownloadBulkContactsModal}
                />
                <DeleteContact
                    contactType={tabValue === 'people' ? ContactType.PERSON : ContactType.COMPANY}
                    selectedIds={tabValue === 'people' ? peopleSelectedIds : companiesSelectedIds}
                    setSelectedIds={tabValue === 'people' ? setPeopleSelectedIds : setCompaniesSelectedIds}
                    showModal={showDeleteContactModal}
                    setShowModal={setShowDeleteContactModal}
                />
                <DeleteBulkContacts
                    section="verify-list"
                    contactType={tabValue === 'people' ? ContactType.PERSON : ContactType.COMPANY}
                    filters={tabValue === 'people' ? peopleFilters : companiesFilters}
                    setFilters={tabValue === 'people' ? setPeopleFilters : setCompaniesFilters}
                    showModal={showDeleteBulkContactsModal}
                    setShowModal={setShowDeleteBulkContactsModal}
                />
            </div>
            <ConfirmModal
                loading={restoreCompanyLoading || restorePersonsLoading}
                title={"Ripristina contatti"}
                label={"Cliccando conferma tutti i contatti selezionati verranno ripristinati nelle rispettive liste."}
                confirmAction={function (): void {
                    tab === 'people' ?
                        handleRestorePersons() :
                        handleRestoreCompanies()
                }}
                onClose={function (): void {
                    dispatch(setRestoreContactModal(false))
                }}
                visible={databaseState.restoreContactModal}
            />
        </Layout>
    )
}